import React, { useContext, useState } from 'react';

import styled from 'styled-components';

import { isBookingEvent, isJobEvent } from '@libs/EventTypes';
import { getMoment } from '@libs/NwMoment';
import { showTime, isAllDay } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';

import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useAddModelToDate } from '@hooks/API/Event/UseEvent';

import { AcknowledgmentDot } from '@components/AcknowledgmentDot/AcknowledgmentDot';
import EventTypeTag from '@components/EventDetails/EventTypeTag';
import { NwButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import NwEllipsis from '@components/Gui/NwEllipsis';
import NwIcon from '@components/Gui/NwIcon';
import ProjectLine from '@components/ProjectLine/ProjectLine';
import EventTimeDisplay from '@components/Schedule/Agenda/EventTimeDisplay';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const AvailableEventContainer = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    border-bottom: ${(props) => props.theme.DefaultBorder};
    min-height: 3rem;
    ${(props) => (props.cancel ? props.theme.RedLineThrough(1) : 'text-decoration: none;')}

    @media only screen and (max-width: 480px) {
        flex-wrap: wrap;
        margin-bottom: 1rem;
    }

    /* &:hover {
        background-color: ${(props) => props.theme.SelectedBackgroundColor};
    } */

    .agenda-event-time {
        text-transform: uppercase;
        padding: 0.25em 0.5em 0.25em 0;
        white-space: nowrap;
        min-width: 8.5rem;
        text-align: right;
        @media only screen and (max-width: 480px) {
            text-align: left;
            width: 100%;
            padding: 0.25em 0.5em;
            border-bottom: ${(props) => props.theme.DefaultBorder};
        }
        .start-end-time {
            display: flex;
            flex-direction: column;

            @media only screen and (max-width: 480px) {
                flex-direction: row;
                .start-time {
                    margin-right: 10px;
                }
            }
        }
        span {
            display: block;
            font-size: 1rem;

            &.start-time {
                font-weight: bold;
            }
            &.all-day {
                font-weight: normal;
                font-size: ${(props) => props.theme.FontSizeSm};
            }
            &.reminder {
                font-weight: bold;
                color: ${(props) => props.theme.DangerColor};
                font-size: 0.6rem;
            }
        }
        small {
            display: block;
            font-weight: normal;
            font-size: ${(props) => props.theme.FontSizeSm};
        }
        div.end-date {
            display: flex;
            justify-content: flex-end;
            align-items: center;

            svg {
                margin-right: 1em;
            }
        }
        div.started-date {
            display: block;
        }
    }

    .agenda-event-content {
        border-left: ${(props) => props.theme.DefaultBorder};
        padding: 0.5em;
        min-height: 3rem;
        flex: 1 0 0;

        @media only screen and (max-width: 480px) {
            width: 100%;
            border-left: none;
        }

        .agenda-event-name {
            display: flex;
            align-items: flex-start;
            justify-content: flex-start;
            flex-direction: column;
            padding: 0.15rem 0;

            .ant-tag {
                ${(props) => (props.cancel ? props.theme.RedLineThrough(1) : 'text-decoration: none;')}
            }
            .netwalk-event-tag {
                margin-top: 0.25rem;
                margin-bottom: 0.15rem;
            }

            .agenda-event-nametext {
                font-weight: bold;
                font-size: 0.8rem;
                line-height: ${(props) => props.theme.FontSizeSm};
                text-transform: uppercase;
                ${(props) => (props.cancel ? props.theme.RedLineThrough(1) : 'text-decoration: none;')}
            }
        }

        .agenda-event-description {
            padding-right: 2rem;
            font-size: ${(props) => props.theme.FontSizeSm};
            line-height: 1.1em;
        }

        .agenda-event-models-list {
            padding: 0.5rem 0 0.15rem 0;
            font-size: ${(props) => props.theme.FontSizeSm};
            text-transform: capitalize;
            em {
                font-style: normal;
            }
        }
        .agenda-event-models-exceptions {
            padding: 0.15rem 0;
            font-size: ${(props) => props.theme.FontSizeSm};
            text-transform: capitalize;
            strong {
                display: block;
                text-transform: uppercase;
            }
            em {
                font-style: normal;
            }
        }
    }
`;

const AgendaEventToolbar = styled.div`
    padding-top: 0.5rem;
    padding-left: 0.5rem;
    padding-right: 0.25rem;
`;

const PreviewButton = styled.div`
    padding: 0.5rem;
    display: flex;
    font-size: 1.5rem;
    align-items: flex-start;
    cursor: pointer;
`;

const AvailableEvent = ({ event, modelID, date, addModelsAs, onAddEvent, onEventClick }) => {
    const { mutateAsync: onAddModelDate } = useAddModelToDate();
    const [loading, setLoading] = useState(false);

    const onAddModelEvent = async (event) => {
        try {
            const params = {
                eventDateId: event.EventDateID,
                modelId: modelID,
            };
            if (isJobEvent(event.TypeName) && addModelsAs > 0) {
                params.optionNumber = addModelsAs;
            }
            await onAddModelDate(params);
            onAddEvent();
        } catch (error) {
            console.error('Add Model Event Error => ', error);
        }
    };

    const { cachedList } = useContext(ListContext);

    const eventtype = cachedList.eventTypes.items.find((et) => et.code === event.TypeName);

    const handleAddModelEvent = async () => {
        setLoading(true);
        await onAddModelEvent(event);
        setLoading(false);
    };

    const renderModelsAndExceptions = () => {
        if (!isBookingEvent(event.TypeName)) {
            return event.ModelID ? <div>{getContactName(event.ModelID)}</div> : '';
        } else {
            const exceptions = event.ModelsDates.filter((modelDate) => modelDate.isException);
            if (exceptions.length === 0) {
                let modelList = [];
                for (const modelDate of event.ModelsDates) {
                    for (const modelofdate of modelDate.Models) {
                        if (!modelList.find((item) => item.ModelID === modelofdate.ModelID)) {
                            modelList.push(modelofdate);
                        }
                    }
                }
                return (
                    <div className="agenda-event-models-list">
                        {modelList.map((model, index) => (
                            <>
                                <span
                                    key={model.ModelID}
                                    style={{ textDecoration: model.Cancel ? 'line-through' : null }}
                                >
                                    <AcknowledgmentDot ackn={model.Acknowledgment} mg="0 .15rem 0 0" />
                                    {getContactName(model.ModelID)}
                                </span>
                                {index < modelList.length - 1 && <em>, </em>}
                            </>
                        ))}
                    </div>
                );
            } else {
                return event.ModelsDates.map((exception, index) => (
                    <div key={index} className="agenda-event-models-exceptions">
                        <strong>
                            {isAllDay(exception.FromDate, exception.ToDate)
                                ? 'all day'
                                : `${showTime(exception.FromDate)} > ${showTime(exception.ToDate)}`}
                        </strong>
                        {exception.CallTime && <strong>Call Time: {exception.CallTime}</strong>}
                        {exception.Models.map((model, index) => (
                            <>
                                <span
                                    key={model.ModelID}
                                    style={{ textDecoration: model.Cancel ? 'line-through' : null }}
                                >
                                    <AcknowledgmentDot ackn={model.Acknowledgment} mg="0 .15rem 0 0" />
                                    {getContactName(model.ModelID)}
                                </span>
                                {index < exception.Models.length - 1 && <em>, </em>}
                            </>
                        ))}
                    </div>
                ));
            }
        }
    };

    return (
        <AvailableEventContainer cancel={event.Cancel}>
            <AgendaEventToolbar>
                <NwButton
                    primary
                    size="small"
                    icon={byPrefixAndName.fass['plus']}
                    loading={loading}
                    onClick={handleAddModelEvent}
                />
            </AgendaEventToolbar>
            <EventTimeDisplay event={event} eventType={eventtype} currentDate={getMoment(date)} />
            <div className="agenda-event-content">
                {event.Project && <ProjectLine small project={event.Project} ellipsis mg="0" />}
                <div className="agenda-event-name">
                    <EventTypeTag event={event} />
                    <div className="agenda-event-nametext">{event.Name}</div>
                </div>
                {eventtype.id !== 150 && (
                    <div className="agenda-event-description">
                        <NwEllipsis noFontSize text={event.Description} rows={2} />
                    </div>
                )}
                <div className="agenda-event-models">{renderModelsAndExceptions()}</div>
            </div>
            <PreviewButton onClick={() => onEventClick(event.ID)}>
                <NwIcon icon={byPrefixAndName.fasl['sidebar-flip']} />
            </PreviewButton>
        </AvailableEventContainer>
    );
};

export default AvailableEvent;
